/**
 * RTWO Native Currency Engine — Supply State Service
 */

import type { CurrencyId, RTWOSupplyState, SupplyStateId } from '../domain-models';
import type { ISupplyStateService, SupplyStateResult } from '../service-interfaces';
import type { NATVRepositories } from '../repositories/natv-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { NATVInternalEventTypes } from '../events/natv-event-bus';
import { BaseNATVService, type NATVServiceContext } from './base-natv-service';

export class SupplyStateService extends BaseNATVService implements ISupplyStateService {
  readonly serviceId = 'natv-supply-state-001';
  readonly serviceName = 'Supply State Service';
  readonly description =
    'Supply state service — placeholder, requires real Ledger Engine connection. No fake total supply, circulating supply, staked supply, or treasury balance.';
  readonly integrations = ['Core Engine', 'Ledger Engine', 'Indexer Engine', 'Event Bus'];
  readonly requiresGenesisConfiguration = true;
  readonly requiresLedgerConnection = true;

  constructor(
    context: NATVServiceContext,
    private readonly repositories: NATVRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_ledger_connection';
  }

  async querySupplyState(currencyId: CurrencyId): Promise<SupplyStateResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        supplyStateId: null,
        error:
          'Supply state query requires real Ledger Engine connection — Waiting for Real Ledger Data',
        isPlaceholder: true,
        source: 'Native Currency Engine — Ledger Engine — Waiting for Real Data',
      };
    }

    const adapterResult = await this.ledgerRegistry.getActiveAdapter().querySupplyState(currencyId);

    if (adapterResult?.supplyState) {
      this.repositories.supplyStates.save(adapterResult.supplyState);
      this.context.eventBus.emit(NATVInternalEventTypes.SUPPLY_STATE_QUERIED, {
        supplyStateId: adapterResult.supplyState.supplyStateId,
        currencyId,
      });
    }

    return {
      success: false,
      supplyStateId: null,
      error:
        'Supply state query requires real Ledger Engine connection — Waiting for Real Ledger Data',
      isPlaceholder: true,
      source: 'Native Currency Engine — Ledger Engine — Waiting for Real Data',
    };
  }

  getSupplyStateByCurrencyId(currencyId: CurrencyId): RTWOSupplyState | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.supplyStates.findByCurrencyId(currencyId);
  }

  getSupplyState(supplyStateId: SupplyStateId): RTWOSupplyState | null {
    if (!this.ledgerRegistry.isConfigured()) return null;
    return this.repositories.supplyStates.findById(supplyStateId);
  }

  getAllSupplyStates(): RTWOSupplyState[] {
    if (!this.ledgerRegistry.isConfigured()) return [];
    return this.repositories.supplyStates.findAll();
  }
}
